import { useQuery } from '@tanstack/react-query';
import { getSettings } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { useUserStore } from '../store/userStore';

interface DashboardHeaderProps {
  title: string;
  roleLabel: string;
  onBackToToday?: () => void;
}

export function DashboardHeader({ title, roleLabel, onBackToToday }: DashboardHeaderProps) {
  const { sessionConfig } = useUserStore();
  const { data: settings } = useQuery({
    queryKey: queryKeys.settings,
    queryFn: getSettings,
    staleTime: 5 * 60 * 1000,
  });

  const companyName = settings?.entityName ?? sessionConfig?.entityName;
  const logoUrl = settings?.logoUrl ?? sessionConfig?.orgLogoUrl;

  return (
    <div className="mb-8">
      {onBackToToday && (
        <button
          onClick={onBackToToday}
          className="flex items-center gap-1.5 mb-4 text-meta text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors"
        >
          <span aria-hidden>←</span>
          Today
        </button>
      )}
      <div className="flex items-end justify-between gap-4">
        {/* Role kicker + dashboard title — briefing pattern: quiet eyebrow, loud headline */}
        <div className="min-w-0">
          <p className="text-micro font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
            {roleLabel}
          </p>
          <h1 className="text-headline text-gray-900 dark:text-white font-display">
            {title}
          </h1>
        </div>

        {/* Company identity */}
        {(logoUrl || companyName) && (
          <div className="flex items-center gap-3 flex-shrink-0 pb-1">
            {logoUrl && (
              <img
                src={logoUrl}
                alt={companyName ?? 'Company logo'}
                className="h-10 w-10 rounded-xl object-cover border border-gray-200 dark:border-white/10 flex-shrink-0"
              />
            )}
            {companyName && (
              <span className="hidden sm:inline text-meta font-semibold text-gray-500 dark:text-gray-400 tracking-tight">
                {companyName}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
